import { motion } from "framer-motion";
import { ArrowUpRight, Megaphone, Search, BrainCircuit, Target } from "lucide-react";
import { Reveal, SectionHeading, EASE } from "./motionPrimitives";
import { scrollToHash } from "./Navbar";

const SERVICES = [
    {
        id: "service-card-marketing",
        tag: "Marketing",
        title: "Performance Marketing",
        desc: "Full-funnel paid campaigns across Google, Meta and LinkedIn — planned, launched and optimized around one metric that matters: return on ad spend.",
        bullets: ["Paid search & social", "Creative testing", "Conversion tracking"],
        icon: Megaphone,
        image:
            "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1200&q=80",
        span: "lg:col-span-2",
    },
    {
        id: "service-card-seo",
        tag: "SEO",
        title: "Search Engine Optimization",
        desc: "Technical audits, content architecture and authority building that put you in front of buyers at the exact moment they search.",
        bullets: ["Technical SEO", "Content strategy", "Local & national SEO"],
        icon: Search,
        span: "",
    },
    {
        id: "service-card-ai-integration",
        tag: "AI Integration",
        title: "AI Integration & Automation",
        desc: "Custom AI agents, chatbots and workflow automations wired directly into your CRM — so your team spends time closing, not copy-pasting.",
        bullets: ["AI sales agents", "Workflow automation", "CRM integration"],
        icon: BrainCircuit,
        image:
            "https://images.unsplash.com/photo-1686061594225-3e92c0cd51b0?auto=format&fit=crop&w=1200&q=80",
        span: "",
    },
    {
        id: "service-card-lead-generation",
        tag: "Lead Generation",
        title: "High-Intent Lead Generation",
        desc: "Multi-channel outbound and inbound pipelines engineered to keep your calendar full of sales-ready conversations — not cold tire-kickers.",
        bullets: ["Outbound systems", "Landing funnels", "Lead scoring & nurturing"],
        icon: Target,
        image:
            "https://images.unsplash.com/photo-1556761175-b413da4baf72?auto=format&fit=crop&w=1200&q=80",
        span: "lg:col-span-2",
    },
];

const ServiceCard = ({ s, i }) => {
    const Icon = s.icon;
    return (
        <motion.article
            data-testid={s.id}
            initial={{ opacity: 0, y: 36 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.7, delay: (i % 3) * 0.1, ease: EASE }}
            className={`group relative bg-white rounded-3xl border border-[#E2E8F0] p-8 flex flex-col hover:border-gold/70 hover:shadow-2xl hover:shadow-navy/10 hover:-translate-y-1 transition-all duration-300 ${s.span}`}
        >
            <ArrowUpRight
                size={22}
                className="absolute top-7 right-7 text-gold opacity-0 -translate-x-1 translate-y-1 group-hover:opacity-100 group-hover:translate-x-0 group-hover:translate-y-0 transition-all duration-300"
            />
            <div className="flex items-center gap-4">
                <span className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-gold/10 text-gold">
                    <Icon size={22} />
                </span>
                <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-steel">
                    {s.tag}
                </span>
            </div>
            <h3 className="mt-6 font-display text-2xl font-bold text-navy">
                {s.title}
            </h3>
            <p className="mt-3 text-steel leading-relaxed">{s.desc}</p>
            <ul className="mt-6 space-y-2">
                {s.bullets.map((b) => (
                    <li key={b} className="flex items-center gap-3 text-sm font-medium text-navy">
                        <span className="w-1.5 h-1.5 rotate-45 bg-gold shrink-0" />
                        {b}
                    </li>
                ))}
            </ul>
            {s.image && (
                <div className="mt-8 rounded-2xl overflow-hidden h-44">
                    <img
                        src={s.image}
                        alt={s.title}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                </div>
            )}
        </motion.article>
    );
};

export default function Services() {
    return (
        <section id="services" className="bg-fog py-20 lg:py-28">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
                    <SectionHeading
                        eyebrow="What we do"
                        title={
                            <>
                                Four disciplines.
                                <br />
                                One growth engine.
                            </>
                        }
                    />
                    <Reveal delay={0.15} className="max-w-sm">
                        <p className="text-steel leading-relaxed">
                            Every engagement is custom-built around your funnel,
                            your market and your margins — then run by one
                            senior team, end to end.
                        </p>
                    </Reveal>
                </div>

                <div className="mt-14 grid gap-5 lg:grid-cols-3">
                    {SERVICES.map((s, i) => (
                        <ServiceCard key={s.id} s={s} i={i} />
                    ))}
                </div>

                <Reveal delay={0.1} className="mt-10">
                    <button
                        data-testid="services-contact-link"
                        onClick={() => scrollToHash("#contact")}
                        className="inline-flex items-center gap-2 font-semibold text-navy border-b-2 border-gold pb-1 hover:text-gold transition-colors"
                    >
                        Tell us what you're building
                        <ArrowUpRight size={18} />
                    </button>
                </Reveal>
            </div>
        </section>
    );
}
